import { useState, useEffect } from "react";
import styles from "./Subscription.module.css";
import { useUser } from "../../contexts/UserContext";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/apiService";

import AddIcon from "../assets/add-icon.png";
import DownArrow from "../assets/down-arrow.png";
import PaymentIcon from "../assets/payment-icon.png";
import carBackground from "../assets/backgroundCar.png";

export default function Subscription() {
  const [citiesList, setCitiesList] = useState([]);
  const [chosenCities, setChosenCities] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCities, setFilteredCities] = useState([]);
  const navigate = useNavigate();
  const { currentUser, loading } = useUser();

  const [checkingNavigate, setCheckingNavigate] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errMessage, setErrMessage] = useState("");
  const [amountPerCity, setAmountPerCity] = useState(0);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  useEffect(() => {
    if (loading || (!loading && !currentUser)) {
      return navigate("/", { replace: true });
    }

    const navigateToPendingStage = async (validation_states) => {
      await new Promise((r) => setTimeout(r, 500));
      if (validation_states.details.status === "pending") {
        return navigate("/dealer-onboarding/dealer-details");
      } else if (validation_states.payment.status === "pending") {
        setCheckingNavigate(false);
        return;
      } else if (validation_states.documents.status === "pending") {
        return navigate("/dealer-onboarding/upload-docs");
      } else if (
        validation_states.payment.status === "validated" ||
        validation_states.contract.status === "pending"
      ) {
        return navigate("/dealer-onboarding/contract");
      }
    };

    const fetchDealerDetails = async () => {
      try {
        const response = await apiService.dealerOnboardingDetails({
          action: "get",
        });
        if (!response.data) {
          console.error("No data received from dealerOnboardingDetails API");
          return;
        }

        if (response.data.error) {
          console.error(
            "Error fetching dealer details:",
            response.data.message
          );
          return;
        }

        const validation_states = response.data.stage_validation_states;
        await navigateToPendingStage(validation_states);
        setAmountPerCity(response.data.amount_per_city || 0);
      } catch (error) {
        console.error("Error fetching dealer details:", error);
      }
    };

    fetchDealerDetails();

    const scriptId = "razorpay-checkout-js";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      document.body.appendChild(script);
    }

    const fetchCities = async () => {
      try {
        const response = await apiService.dealerOnboardingGetCities();
        if (response.data && response.data.cities) {
          setCitiesList(response.data.cities);
          setFilteredCities(response.data.cities);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    fetchCities();
  }, [currentUser, loading, navigate]);

  // Handle typing in search box
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim() === "") {
      const filtered = citiesList.filter(
        (city) => !chosenCities.includes(city)
      );
      setFilteredCities(filtered);
    } else {
      const filtered = citiesList.filter(
        (city) =>
          city?.name?.toLowerCase().includes(value.toLowerCase()) &&
          !chosenCities.includes(city)
      );
      setFilteredCities(filtered);
    }
  };

  // Add selected city
  const handleAddCity = (city) => {
    if (city && !chosenCities.includes(city)) {
      setChosenCities([...chosenCities, city]);
      setSearchTerm("");
      setFilteredCities([]);
    }
  };

  // Remove chosen city
  const handleRemoveCity = (city) => {
    setChosenCities(chosenCities.filter((c) => c !== city));
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      setErrMessage("You must accept the terms and conditions to proceed.");
      return;
    }
    if (chosenCities.length === 0) {
      setErrMessage("Please select at least one city to proceed.");
      return;
    }
    setErrMessage("");
    setIsProcessingPayment(true);

    try {
      const response = await apiService.dealerOnboardingPayment({
        cities: chosenCities.map((c) => c.id),
      });

      if (response.data && response.data.order_info) {
        const orderInfo = response.data.order_info;
        const rzp = new window.Razorpay({
          ...orderInfo,
          modal: {
            escape: false,
            ondismiss: () => {
              setIsProcessingPayment(false);
            },
          },
          handler: async (response) => {
            const verifyResponse = await apiService.dealerOnboardingPayment({
              action: "verify",
              payment_id: response.razorpay_payment_id,
              order_id: response.razorpay_order_id,
              signature: response.razorpay_signature,
            });

            if (verifyResponse.data && verifyResponse.data.error === false) {
              setIsProcessingPayment(false);
              navigate("/dealer-onboarding/upload-docs");
            } else {
              alert("Payment verification failed.");
            }
          },
        });

        rzp.on("payment.failed", async (response) => {
          setIsProcessingPayment(false);
          console.log(response.error.description);

          const failResponse = await apiService.dealerOnboardingPayment({
            action: "report_error",
            error: response.error,
          });

          if (failResponse?.data?.error) {
            console.error("Error reporting failed payment:", failResponse.data);
          }
        });

        rzp.open();
      }
    } catch (error) {
      console.error("Error initiating payment:", error);
      alert("Error initiating payment. Please try again.");
      setIsProcessingPayment(false);
    }
  };

  return checkingNavigate ? (
    <div className={`container ${styles.container}`}>
      <div className={styles.checkNavigate}>Please wait...</div>
    </div>
  ) : (
    <div className={`container ${styles.container}`}>
      <div className={styles.wrapper}>
        <div className={styles.left}>
          <div className={styles.subscriptionInfoWrapper}>
            <h1 className={styles.title}>Choose Your Subscription</h1>
            <h2>1 year Access to Auction Platform</h2>

            <div className={styles.subscriptionInfo}>
              <div className={styles.chosenCities}>
                {/* show chosen cities */}
                {chosenCities.map((city) => (
                  <div key={city.id} className={styles.chosenCity}>
                    {city.name}
                    <img
                      src={AddIcon}
                      alt="Remove"
                      onClick={() => handleRemoveCity(city)}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                ))}

                {/* input and dropdown */}
                <div className={styles.chooseCityInput}>
                  <div className={styles.inputWithIcon}>
                    <input
                      type="text"
                      placeholder="Select the cities you want to subscribe to"
                      value={searchTerm}
                      onChange={handleSearch}
                      onFocus={handleSearch}
                    />
                    <img src={DownArrow} alt="Dropdown" />
                  </div>
                  {filteredCities.length > 0 && (
                    <ul className={styles.dropdown}>
                      {filteredCities.map((city) => (
                        <li
                          key={city.id}
                          onClick={() => handleAddCity(city)}
                          className={styles.dropdownItem}
                        >
                          {city.name}
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* <button
                    className={styles.addCityButton}
                    onClick={() => handleAddCity(searchTerm)}
                  >
                    Add City
                  </button> */}
                </div>
              </div>

              <div className={styles.totalAmountDetails}>
                <div>
                  <h2>Total Amount</h2>
                  <h3>₹ {chosenCities.length * amountPerCity}</h3>
                </div>

                <div>
                  <div className={styles.disclaimer}>
                    <input
                      type="checkbox"
                      name="terms-and-conditions"
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    <p>
                      I understand that If the application fails after the
                      verification of documents for whatsoever reason refund
                      will be initiated minus the admin charges
                    </p>
                  </div>
                  {errMessage?.trim() !== "" && (
                    <p className={styles.errMessage}>
                      {errMessage ||
                        "You must accept the terms and conditions to proceed."}
                    </p>
                  )}
                </div>

                <button
                  disabled={isProcessingPayment}
                  className={styles.PaymentBtn}
                  onClick={handlePayment}
                >
                  <img src={PaymentIcon} alt="Payment" />
                  {isProcessingPayment ? "Processing..." : "Make Payment"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.right}>
          <img src={carBackground} alt="Car Background" />
        </div>
      </div>
    </div>
  );
}
